import { useState } from "react"
import type { ReactNode } from "react"
import { api } from "../../api/client.ts"
import type {
  Case,
  CaseDetailResponse,
  CaseLine,
  Disposition,
  FreightType,
} from "../../api/types.ts"
import { useT } from "../../i18n/index.tsx"
import type { Translator } from "../../i18n/translate.ts"
import { useSession } from "../../auth/SessionProvider.tsx"
import { availableCaseActions, type CaseAction, type CaseActionId } from "../../lib/case-actions.ts"
import { CASE_OWNERS, OWNER_ICON } from "../../lib/case-owner.ts"
import { toDateString } from "../../lib/date-presets.ts"
import { DEFAULT_DISPOSITION } from "../../lib/dispositions.ts"
import { DEFAULT_FREIGHT } from "../../lib/freight.ts"
import { Button, Card, CardHeader, Field, Icon, Input, Select, useToast } from "../../ui/index.ts"
import { DispositionOptions, FreightOptions } from "../choice-options.tsx"

/**
 * "지금 할 일" — 이 사람이 이 건에 할 수 있는 작업만 모은 카드.
 *
 * 무엇을 할 수 있는지는 `lib/case-actions.ts` 가 정한다(테스트가 지킨다).
 * 여기서는 그 결과를 조각으로 바꿔 그리고 서버를 부르기만 한다.
 *
 * 조각마다 따로 컴포넌트를 둔다 — 적어 넣는 칸(중량·사유·날짜)의 상태가 그 조각 안에만 있고,
 * 보이지 않는 조각의 상태는 아예 만들어지지 않는다.
 */
type ActionProps = {
  caseId: number
  item: Case
  lines: CaseLine[]
  busy: boolean
  blockedBy: string[]
  /** 서버를 부르고, 끝나면 다시 읽고 토스트를 띄운다. */
  call: (action: () => Promise<unknown>) => void
  t: Translator
}

const Row = ({ children }: { children: ReactNode }) => <div className="next__row">{children}</div>

function blockedReason(t: Translator, blockedBy: string[]): string {
  return blockedBy.map((code) => t(`blocker.${code}`)).join(" ")
}

function ConfirmDocAction({ caseId, busy, blockedBy, call, t }: ActionProps) {
  const reason = blockedReason(t, blockedBy)
  return (
    <Row>
      <Button
        variant="primary"
        disabled={busy || blockedBy.length > 0}
        title={reason}
        onClick={() => call(() => api.post(`/api/cases/${caseId}/doc/confirm`))}
      >
        {t("caseDetail.confirmDoc")}
      </Button>
      {blockedBy.length > 0 ? <span className="next__blocked">{reason}</span> : null}
    </Row>
  )
}

function ArriveAction({ caseId, item, busy, call, t }: ActionProps) {
  const [trackingNo, setTrackingNo] = useState("")
  // 번호가 이미 있으면 그대로 입고만 한다. 없으면 상자에 적힌 번호를 함께 채운다.
  const needsTracking = item.trackingNo === null
  return (
    <Row>
      {needsTracking ? (
        <Field label={t("case.trackingNo")} htmlFor="next-arrive-tracking">
          <Input
            id="next-arrive-tracking"
            inputMode="numeric"
            value={trackingNo}
            onChange={(event) => setTrackingNo(event.target.value)}
          />
        </Field>
      ) : null}
      <Button
        variant="primary"
        icon="check"
        disabled={busy}
        onClick={() =>
          call(() =>
            api.post(`/api/cases/${caseId}/arrive`, {
              trackingNo: trackingNo.trim() === "" ? null : trackingNo.trim(),
            }),
          )
        }
      >
        {t("caseDetail.arriveManually")}
      </Button>
    </Row>
  )
}

/**
 * ④ 검수 — 줄마다 실제로 몇 개 왔는지 센다.
 *
 * 모든 줄을 세야 저장된다(서버도 같은 판정을 한다). 일부만 적으면 "아직 안 봤다"와
 * "0개 왔다"를 구별할 수 없기 때문이다.
 */
function InspectAction({ caseId, lines, busy, call, t }: ActionProps) {
  const [counts, setCounts] = useState<Record<number, string>>(() =>
    Object.fromEntries(
      lines.map((line) => [line.id, line.receivedQty === null ? "" : String(line.receivedQty)]),
    ),
  )
  const [note, setNote] = useState("")

  const filled = lines.every((line) => /^\d+$/.test(counts[line.id] ?? ""))

  return (
    <div className="next__block">
      {lines.map((line) => (
        <Row key={line.id}>
          <Field
            label={`${line.description} · ${t("lines.qty")} ${line.qty}`}
            htmlFor={`inspect-${line.id}`}
          >
            <Input
              id={`inspect-${line.id}`}
              inputMode="numeric"
              value={counts[line.id] ?? ""}
              onChange={(event) =>
                setCounts({ ...counts, [line.id]: event.target.value.replace(/\D/g, "") })
              }
            />
          </Field>
        </Row>
      ))}
      <Row>
        <Field label={t("caseDetail.inspectNote")} htmlFor="inspect-note">
          <Input id="inspect-note" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        <Button
          variant="primary"
          disabled={busy || !filled}
          onClick={() =>
            call(() =>
              api.post(`/api/cases/${caseId}/inspect`, {
                lines: lines.map((line) => ({ id: line.id, receivedQty: Number(counts[line.id]) })),
                note: note || null,
              }),
            )
          }
        >
          {t("caseDetail.inspect")}
        </Button>
      </Row>
    </div>
  )
}

function WeightAction({ caseId, item, busy, call, t }: ActionProps) {
  const [weight, setWeight] = useState(item.weightG === null ? "" : String(item.weightG))
  return (
    <Row>
      <Field label={t("scan.weightPrompt")} htmlFor="next-weight">
        <Input
          id="next-weight"
          inputMode="numeric"
          value={weight}
          onChange={(event) => setWeight(event.target.value)}
        />
      </Field>
      <Button
        disabled={busy || !/^\d+$/.test(weight)}
        onClick={() =>
          call(() =>
            api.post(`/api/cases/${caseId}/weight`, { grams: Number(weight), source: "manual" }),
          )
        }
      >
        {t("scan.saveWeight")}
      </Button>
    </Row>
  )
}

function FreightAction({ caseId, item, busy, call, t }: ActionProps) {
  // 아직 적지 않았으면 착불로 서 있는다 — 고객이 부치는 상자는 거의 다 착불이다.
  const [freightType, setFreightType] = useState<FreightType>(item.freightType ?? DEFAULT_FREIGHT)
  const [codFee, setCodFee] = useState(item.codFeeJpy === null ? "" : String(item.codFeeJpy))
  return (
    <Row>
      <Field label={t("case.freight")} htmlFor="next-freight">
        <Select
          id="next-freight"
          value={freightType}
          disabled={busy}
          onChange={(event) => setFreightType(event.target.value as FreightType)}
        >
          <FreightOptions />
        </Select>
      </Field>

      {/* 착불이면 금액을 여기서 적고 고친다(입고 스캔 화면에서 적은 값이 들어 있다). */}
      {freightType === "collect" ? (
        <Field label={t("case.codFee")} htmlFor="next-cod-fee">
          <Input
            id="next-cod-fee"
            inputMode="numeric"
            value={codFee}
            onChange={(event) => setCodFee(event.target.value.replace(/\D/g, ""))}
          />
        </Field>
      ) : null}

      <Button
        disabled={busy}
        onClick={() =>
          call(() =>
            api.post(`/api/cases/${caseId}/freight`, {
              freightType,
              // 선불이면 착불비를 적지 않는다(서버가 거부한다).
              // 착불이면 적어 둔 금액을 보낸다(0 은 "0엔 착불"이라 빈 값과 다르다).
              codFeeJpy: freightType === "collect" && codFee !== "" ? Number(codFee) : null,
            }),
          )
        }
      >
        {t("caseDetail.save")}
      </Button>
    </Row>
  )
}

function RequestReviewAction({ caseId, busy, call, t }: ActionProps) {
  const [reason, setReason] = useState("")
  return (
    <Row>
      <Field label={t("caseDetail.reviewReason")} htmlFor="next-review">
        <Input
          id="next-review"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>
      <Button
        disabled={busy}
        onClick={() =>
          call(() =>
            api.post(`/api/cases/${caseId}/doc/request-review`, { reason: reason || null }),
          )
        }
      >
        {t("caseDetail.requestReview")}
      </Button>
    </Row>
  )
}

function ConfirmArrivalAction({ caseId, busy, call, t }: ActionProps) {
  return (
    <Row>
      <Button
        disabled={busy}
        icon="check"
        onClick={() => call(() => api.post(`/api/cases/${caseId}/confirm-arrival`))}
      >
        {t("caseDetail.confirmArrival")}
      </Button>
    </Row>
  )
}

function DispositionAction({ caseId, item, busy, call, t }: ActionProps) {
  // 아직 정하지 않았으면 한국반송으로 서 있는다 — 거의 다 한국으로 돌려보낸다.
  // 고른 값은 저장을 눌러야 남는다(폐기는 되돌릴 수 없어 손이 스쳐 정해지면 안 된다).
  const [disposition, setDisposition] = useState<Disposition>(
    item.disposition ?? DEFAULT_DISPOSITION,
  )
  return (
    <Row>
      <Field label={t("caseDetail.setDisposition")} htmlFor="next-disposition">
        <Select
          id="next-disposition"
          value={disposition}
          disabled={busy}
          onChange={(event) => setDisposition(event.target.value as Disposition)}
        >
          <DispositionOptions />
        </Select>
      </Field>
      <Button
        disabled={busy || disposition === item.disposition}
        onClick={() => call(() => api.post(`/api/cases/${caseId}/disposition`, { disposition }))}
      >
        {t("caseDetail.save")}
      </Button>
    </Row>
  )
}

function DiscardAction({ caseId, busy, call, t }: ActionProps) {
  const [closedOn, setClosedOn] = useState(toDateString())
  return (
    <Row>
      <Field label={t("caseDetail.closedOn")} htmlFor="next-closed">
        <Input
          id="next-closed"
          type="date"
          value={closedOn}
          onChange={(event) => setClosedOn(event.target.value)}
        />
      </Field>
      <Button
        variant="danger"
        icon="trash"
        disabled={busy}
        onClick={() => call(() => api.post(`/api/cases/${caseId}/discard`, { closedOn }))}
      >
        {t("caseDetail.discard")}
      </Button>
    </Row>
  )
}

function CancelAction({ caseId, item, busy, call, t }: ActionProps) {
  return (
    <Row>
      <Button
        variant="danger"
        disabled={busy}
        // version 은 낙관적 잠금에 쓰인다. 빠뜨리면 남의 수정을 덮어쓴다.
        onClick={() => call(() => api.post(`/api/cases/${caseId}/cancel`, { version: item.version }))}
      >
        {t("caseDetail.cancelCase")}
      </Button>
    </Row>
  )
}

const VIEWS: Record<CaseActionId, (props: ActionProps) => ReactNode> = {
  arrive: ArriveAction,
  inspect: InspectAction,
  confirmDoc: ConfirmDocAction,
  weight: WeightAction,
  freight: FreightAction,
  requestReview: RequestReviewAction,
  confirmArrival: ConfirmArrivalAction,
  disposition: DispositionAction,
  discard: DiscardAction,
  cancel: CancelAction,
}

export function NextActions({
  caseId,
  data,
  busy,
  run,
}: {
  caseId: number
  data: CaseDetailResponse
  busy: boolean
  run: (action: () => Promise<unknown>) => Promise<void>
}) {
  const t = useT()
  const { user } = useSession()
  const toast = useToast()

  if (!user) return null

  const item = data.case
  const actions: CaseAction[] = availableCaseActions({
    role: user.role,
    status: item.status,
    shipperConfirmedAt: item.shipperConfirmedAt,
    disposition: item.disposition,
    lineCount: data.lines.length,
    inspectedAt: item.inspectedAt,
    blockers: data.blockers,
  })

  if (actions.length === 0) return null

  /** 성공하면 토스트를 띄운다. 긴 화면 아래쪽에서 눌러도 결과가 눈에 들어온다. */
  const call = (action: () => Promise<unknown>) =>
    void run(action).then(() => toast(t("common.saved")))

  // 관리자는 두 쪽 일을 함께 본다 — 누구 일인지 갈라 보여 준다(차례는 lib/case-owner.ts).
  const groups = CASE_OWNERS.map((owner) => ({
    owner,
    actions: actions.filter((action) => action.owner === owner),
  })).filter((group) => group.actions.length > 0)

  // 두 쪽이 함께 보일 때만 담당 색·머리글을 붙인다. 한쪽 일만 보이는 사람(화주·쿨트랙JP)에게는
  // 전부 자기 일이라 가를 것이 없고, 읽을 이름표가 없는 색은 뜻 없는 장식이 된다.
  const split = groups.length > 1

  return (
    <Card>
      <CardHeader title={t("caseDetail.nextActions")} icon="check" />
      <div className={split ? "next next--split" : "next"}>
        {groups.map((group) => (
          <section
            className={split ? `next__group next__group--${group.owner}` : "next__group"}
            key={group.owner}
            aria-label={split ? t(`caseDetail.owner.${group.owner}`) : undefined}
          >
            {split ? (
              <p className="next__owner">
                <Icon name={OWNER_ICON[group.owner]} size={14} className="next__owner-icon" />
                {t(`caseDetail.owner.${group.owner}`)}
                <span className="next__owner-side">{t(`owner.side.${group.owner}`)}</span>
              </p>
            ) : null}
            <div className="next__body">
              {group.actions.map((action) => {
                const View = VIEWS[action.id]
                return (
                  <View
                    key={action.id}
                    caseId={caseId}
                    item={item}
                    lines={data.lines}
                    busy={busy}
                    blockedBy={action.blockedBy}
                    call={call}
                    t={t}
                  />
                )
              })}
            </div>
          </section>
        ))}
      </div>
    </Card>
  )
}
